import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Part } from '../interface/part';
import { DataService } from '../service/data.service';
import { PartService } from '../service/part.service';
import { MkgOSPart } from '../class/mkg-os-part';
import { LOG_REPORTER_KEYS, LogReporterService } from '@app/service/log-reporter.service';
import { MkgOS } from '../class/mkg-os';
import { MoveTypes } from '../shared/lists/move-type';
import { MovementService } from '../service/movement.service';
import { OperationFunction } from '../shared/lists/operation-functions';
import { NF_response } from '../interface/NF-response';
import { Operation } from '../interface/operation';
import { InvoiceItem } from '../interface/invoice-item';
import { OrderFull } from '../component/page/company/order/model/order.model';
import { OrderItem } from '../component/page/company/order/model/order-item';
import { IOrder } from '../component/page/company/order/interface/order.interface';
import { IOrderItem } from '../component/page/company/order/interface/order-item.interface';
import { FieldChanged } from '../shared/type-aliases/field-changed';
import { IssueOrderInvoiceResponse } from '@app/component/page/company/order/service/issue-order-invoice.service';
import { IssueOsInvoiceResponse } from '@app/component/page/company/os/services/invoice-os.service';
import { StockMoveError } from '@app/class/stock-move/stock-move-error';
import moment from 'moment';
import Decimal from 'decimal.js';


/** Handle stock of part */
@Injectable({
  providedIn: 'root'
})
export class StockController {

  constructor(
    private partService: PartService,
    private snackbar: MatSnackBar,
    private translateService: TranslateService,
    private router: Router,
    private dataService: DataService,
    private _logReporter: LogReporterService,
    private _movementService: MovementService,
  ) {
  }

  public async onBudgetTurnedIntoOS(osPart: MkgOSPart, movementData: {
    OS: MkgOS;
    movementCaller: string;
    operationFunction: OperationFunction;
    isSupplementaryApproved: boolean;
  }): Promise<void> {
    const { OS, movementCaller, operationFunction, isSupplementaryApproved } = movementData

    const partId = typeof osPart.part === "string" ? osPart.part : osPart.part.id;

    let stockMoveError: StockMoveError,
        part: Part,
        availableStockBeforeUpdate: number,
        balanceBeforeUpdate: number,
        osStockBeforeUpdate: number,
        orderStockBeforeUpdate: number;

    try {
      part = await this.partService.get(partId);

      availableStockBeforeUpdate = part.availableStock;
      balanceBeforeUpdate = part.stock;
      osStockBeforeUpdate = part.osStock;
      orderStockBeforeUpdate = part.orderStock;

      const isMultiBranch = this.dataService.isMultiBranch();
      
      if (isMultiBranch) {
        // Multi-branch: update BranchPart via API
        await this.partService.adjustBranchPartStock(partId, osPart.amount(), 'add', 'osStock');
      } else {
        // Simple company: update Parts directly
        part.osStock = (part.osStock || 0) + osPart.amount();
        this.calcAvailableStockOf(part, movementCaller);
        await this.partService.addOrUpdate(part, "StockController.onBudgetTurnedIntoOS()", true);
        this.verifyStockMinOf(part);
      }
    } catch (error) {
      console.error(error);
      stockMoveError = new StockMoveError(error);
    } finally {
      await this._movementService.createMovementFromOS({
        moveType: isSupplementaryApproved ? MoveTypes.approvedComplementar : MoveTypes.openedRo,
        OS,
        roPart: osPart,
        operationFunction,
        part,
        diference: osPart.amount(),
        availableStockBeforeUpdate,
        balanceBeforeUpdate,
        osStockBeforeUpdate,
        orderStockBeforeUpdate,
        error: stockMoveError
      }, movementCaller + "; StockController.onBudgetTurnedIntoOS()");
    }
  }

  public async onOSrevertedToBudget(osPart: MkgOSPart, movementData: {
    OS: MkgOS;
    movementCaller: string;
    operationFunction: OperationFunction;
    isSupplementaryBudget: boolean;
  }): Promise<void> {
    const { OS, movementCaller, operationFunction, isSupplementaryBudget } = movementData;
    const partId = typeof osPart.part === "string" ? osPart.part : osPart.part.id;

    let stockMoveError: StockMoveError,
        part: Part,
        availableStockBeforeUpdate: number,
        balanceBeforeUpdate: number,
        osStockBeforeUpdate: number,
        orderStockBeforeUpdate: number;

    const dayToCompareAndPreventStockErrors = moment("2025-04-01T03:00:00.000Z").startOf("day"),
          shouldMoveStockByDate = isSupplementaryBudget && (osPart.createdAt && osPart.createdAt?.isBefore(dayToCompareAndPreventStockErrors, "hours"));

    try {
      part = await this.partService.get(partId);

      if (!isSupplementaryBudget || shouldMoveStockByDate) {
        availableStockBeforeUpdate = part.availableStock;
        balanceBeforeUpdate = part.stock;
        osStockBeforeUpdate = part.osStock;
        orderStockBeforeUpdate = part.orderStock;
        const isMultiBranch = this.dataService.isMultiBranch();
        
        if (isMultiBranch) {
          // Multi-branch: update BranchPart via API
          await this.partService.adjustBranchPartStock(partId, osPart.amount(), 'subtract', 'osStock');
        } else {
          // Simple company: update Parts directly
          part.osStock = (part.osStock || 0) - osPart.amount();
          this.calcAvailableStockOf(part, movementCaller);
          await this.partService.addOrUpdate(part, "StockController.onOSrevertedToBudget()", true);
          this.verifyStockMinOf(part);
        }
      }
    } catch (error) {
      console.error(error);
      stockMoveError = new StockMoveError(error);
    } finally {
      if (!isSupplementaryBudget || shouldMoveStockByDate) {
        await this._movementService.createMovementFromOS({
          moveType: isSupplementaryBudget ? MoveTypes.rejectedComplementar : MoveTypes.turnedIntoRejected,
          OS,
          roPart: osPart,
          operationFunction,
          part,
          diference: osPart.amount(),
          availableStockBeforeUpdate,
          balanceBeforeUpdate,
          osStockBeforeUpdate,
          orderStockBeforeUpdate,
          error: stockMoveError
        }, movementCaller + "; StockController.onOSrevertedToBudget()");
      }
    }
  }

  // apiMovementToPost nao criado por conta de que iria duplicar a movimentação
  public async onOSfinished(part: Part, amount: number, movementData: {
    value: number;
    operation: Operation;
    invoice: NF_response;
    senderID: string;
    caller: string;
    OS: MkgOS
  }): Promise<Part> {
    const { OS, caller, operation, invoice, value, senderID } = movementData

    let stockMoveError: StockMoveError,
        availableStockBeforeUpdate: number,
        balanceBeforeUpdate: number,
        osStockBeforeUpdate: number,
        orderStockBeforeUpdate: number;

    try {
      availableStockBeforeUpdate = part.availableStock;
      balanceBeforeUpdate = part.stock;
      osStockBeforeUpdate = part.osStock;
      orderStockBeforeUpdate = part.orderStock;

      if (operation.changeStock) {
        const isMultiBranch = this.dataService.isMultiBranch();
        
        if (isMultiBranch) {
          // Multi-branch: update BranchPart via API
          // Subtract from both stock (balance) and osStock
          await this.partService.adjustBranchPartStock(part.id, amount, 'subtract', 'stock');
          await this.partService.adjustBranchPartStock(part.id, amount, 'subtract', 'osStock');
        } else {
          // Simple company: update Parts directly
          part.stock = (part.stock || 0) - amount;
          part.osStock = (part.osStock || 0) - amount;
          this.calcAvailableStockOf(part, caller);
          this.verifyStockMinOf(part);
          await this.partService.addOrUpdate(part, "StockController.onOSfinished()", true);
        }
      }
    } catch (error) {
      console.error(error);
      stockMoveError = new StockMoveError(error);
    } finally {
      await this._movementService.createMovementFromNF({
        part,
        amount,
        value,
        operation,
        invoice,
        senderID,
        caller: caller + "; StockController.onOSfinished()",
        OS,
        availableStockBeforeUpdate,
        balanceBeforeUpdate,
        osStockBeforeUpdate,
        orderStockBeforeUpdate,
        error: stockMoveError
      });

      return part;
    }
  }

  async onBreakDownInvoiceFromOS(data: { roPart: MkgOSPart, OS: MkgOS, movementCaller: string, pastInvoiceRelated: NF_response, operation: Operation }) {
    const errLogger = this._logReporter.errorLogger("StockController.onOSfinished()", LOG_REPORTER_KEYS.STOCK);
    const { OS, movementCaller, roPart, pastInvoiceRelated, operation } = data;
    
    try {
      const part = await this.partService.get(roPart.part.id);
      const txtLogger = this._logReporter.textLogger(`StockController.onOSfinished(partId: ${part.id}, amount: ${roPart.amount()}); previousCallers: ${movementCaller}; StockController.onOSfinished()`, LOG_REPORTER_KEYS.STOCK);

      const loginfo = {
        code: part.code,
        stock: part.stock,
        requestedStock: part.orderStock,
        inOrderStock: part.osStock,
        availableStock: part.availableStock,
        invoiceNumber: pastInvoiceRelated.numero,
        invoiceId: pastInvoiceRelated.id,
        osBudgetCode: OS.budgetCode,
        osNumber: OS.codeSystem,
        partId: part.id
      }

      txtLogger(JSON.stringify({ antes: loginfo }));
      const availableStockBeforeUpdate = part.availableStock,
        balanceBeforeUpdate = part.stock,
        osStockBeforeUpdate = part.osStock,
        orderStockBeforeUpdate = part.orderStock;

      if (operation.changeStock) {
        const amount = roPart.amount();
        
        part.osStock = new Decimal(part.osStock || 0).add(amount).toDecimalPlaces(2).toNumber();
        this.calcAvailableStockOf(part, movementCaller);
        this.verifyStockMinOf(part);
        await this.partService.addOrUpdate(part, "StockController.onOSfinished()", true);
        
        await this._movementService.createMovementFromOS({
          moveType: MoveTypes.breakDownCancelledOrDeniedInvoice,
          OS,
          roPart,
          operationFunction: "Nota desmembrada e OS foi reaberta",
          part,
          diference: roPart.amount(),
          availableStockBeforeUpdate,
          balanceBeforeUpdate,
          osStockBeforeUpdate,
          orderStockBeforeUpdate,
        }, movementCaller + "; StockController.onBudgetTurnedIntoOS()");
      }
    } catch (error) {
      console.error(error);
      errLogger(error);
    }
  }


  public async onEntryInvoiceCreated(part: Part, amount: number, movementData: {
    item: InvoiceItem;
    operation: Operation;
    invoice: Pick<NF_response | IssueOsInvoiceResponse["partInvoice"] | IssueOrderInvoiceResponse["partInvoice"], "numero" | "id" | "serie">;
    senderID: string;
    moveType: MoveTypes;
    caller: string;
    isCanceled: boolean;
    isSupplier: boolean;
    fromXmlImport: boolean;
    OSId?: string;
    amountFromImportedInvoice?: number;
    measurementUnitFromImportedInvoice?: string;
    orderId?: string;
  }): Promise<Part> {
    const {
      isSupplier,
      caller,
      invoice,
      item,
      moveType,
      operation,
      senderID,
      isCanceled,
      OSId,
      orderId,
      amountFromImportedInvoice,
      fromXmlImport,
      measurementUnitFromImportedInvoice,
    } = movementData;

    let stockMoveError: StockMoveError,
        availableStockBeforeUpdate: number,
        balanceBeforeUpdate: number,
        osStockBeforeUpdate: number,
        orderStockBeforeUpdate: number;

    try {
      availableStockBeforeUpdate = part.availableStock;
      balanceBeforeUpdate = part.stock;
      osStockBeforeUpdate = part.osStock;
      orderStockBeforeUpdate = part.orderStock;

      if (operation.changeStock) {
        const isMultiBranch = this.dataService.isMultiBranch();
        
        if (isMultiBranch) {
          // Multi-branch: update BranchPart via API
          await this.partService.adjustBranchPartStock(part.id, amount, 'add', 'stock');
        } else {
          // Simple company: update Parts directly
          part.stock = (part.stock || 0) + amount;
          this.calcAvailableStockOf(part, caller);
          await this.partService.addOrUpdate(part, "StockController.onEntryInvoiceCreated()", true);
        }
      }
    } catch (error) {
      console.error(error);
      stockMoveError = new StockMoveError(error);
    } finally {
      await this._movementService.createMovementFromInvoice({
        caller: caller + "; StockController.onEntryInvoiceCreated()",
        invoice,
        item,
        moveType,
        operation,
        part,
        senderID,
        amount,
        operationFuntion: isCanceled && operation.changeStock ? "Cancelamento" : operation.operationFunction,
        OSId,
        orderId,
        isSupplier,
        availableStockBeforeUpdate,
        balanceBeforeUpdate,
        osStockBeforeUpdate,
        orderStockBeforeUpdate,
        error: stockMoveError,
        fromXmlImport,
        amountFromImportedInvoice,
        measurementUnitFromImportedInvoice,
      })

      return part;
    }
  }

  public async onOutPutInvoiceCreated(partId: string, amount: number, movementData: {
    item: InvoiceItem;
    operation: Operation;
    invoice: Pick<NF_response | IssueOsInvoiceResponse["partInvoice"] | IssueOrderInvoiceResponse["partInvoice"], "numero" | "id" | "serie">;
    senderID: string;
    moveType: MoveTypes;
    caller: string;
    isCanceled: boolean;
    isSupplier: boolean;
    OSId?: string;
    orderId?: string;
  }): Promise<void> {
    const { caller, invoice, item, moveType, operation, senderID, isCanceled, OSId, orderId, isSupplier } = movementData;

    let stockMoveError: StockMoveError,
        part: Part,
        availableStockBeforeUpdate: number,
        balanceBeforeUpdate: number,
        osStockBeforeUpdate: number,
        orderStockBeforeUpdate: number;

    try {
      part = await this.partService.get(partId);
      availableStockBeforeUpdate = part.availableStock;
      balanceBeforeUpdate = part.stock;
      osStockBeforeUpdate = part.osStock;
      orderStockBeforeUpdate = part.orderStock;

      if (operation.changeStock) {
        const isMultiBranch = this.dataService.isMultiBranch();
        
        if (isMultiBranch) {
          // Multi-branch: update BranchPart via API
          await this.partService.adjustBranchPartStock(partId, amount, 'subtract', 'stock');
        } else {
          // Simple company: update Parts directly
          part.stock = (part.stock || 0) - amount;
          this.calcAvailableStockOf(part, caller);
          this.verifyStockMinOf(part);
          await this.partService.addOrUpdate(part, "StockController.onOutPutInvoiceCreated()", true);
        }
      } else {
        // Only save if no stock change but still need to persist
        if (!this.dataService.isMultiBranch()) {
          await this.partService.addOrUpdate(part, "StockController.onOutPutInvoiceCreated()", true);
        }
      }
    } catch (error) {
      console.error(error);
      stockMoveError = new StockMoveError(error);
    } finally {
      await this._movementService.createMovementFromInvoice({
        caller: caller + "; StockController.onOutPutInvoiceCreated()",
        invoice,
        item,
        moveType,
        operation,
        part,
        senderID,
        amount,
        operationFuntion: isCanceled && operation.changeStock ? "Cancelamento" : operation.operationFunction,
        OSId,
        orderId,
        isSupplier,
        availableStockBeforeUpdate,
        balanceBeforeUpdate,
        osStockBeforeUpdate,
        orderStockBeforeUpdate,
        error: stockMoveError,
        fromXmlImport: false
      })
    }
  }

  public async onInvoiceEntryCanceled(partId: string, amount: number, movementData: {
    item: InvoiceItem;
    operation: Operation;
    invoice: NF_response;
    senderID: string;
    moveType: MoveTypes;
    caller: string;
    isSupplier: boolean;
    fromXmlImport: boolean;
    OSId?: string;
    orderId?: string;
    amountFromImportedInvoice?: number;
    measurementUnitFromImportedInvoice?: string;
  }) {
    const {
      caller,
      invoice,
      item,
      moveType,
      operation,
      senderID,
      OSId,
      orderId,
      isSupplier,
      amountFromImportedInvoice,
      fromXmlImport,
      measurementUnitFromImportedInvoice,
    } = movementData;

    let stockMoveError: StockMoveError,
        part: Part,
        availableStockBeforeUpdate: number,
        balanceBeforeUpdate: number,
        osStockBeforeUpdate: number,
        orderStockBeforeUpdate: number;

    try {
      part = await this.partService.get(partId);
      availableStockBeforeUpdate = part.availableStock;
      balanceBeforeUpdate = part.stock;
      osStockBeforeUpdate = part.osStock;
      orderStockBeforeUpdate = part.orderStock;

      if (operation.changeStock) {
        const isMultiBranch = this.dataService.isMultiBranch();
        
        if (isMultiBranch) {
          // Multi-branch: update BranchPart via API (subtract because entry was canceled)
          await this.partService.adjustBranchPartStock(partId, amount, 'subtract', 'stock');
        } else {
          // Simple company: update Parts directly
          part.stock = (part.stock || 0) - amount;
          this.calcAvailableStockOf(part, caller);
          await this.partService.addOrUpdate(part, "StockController.onInvoiceEntryCanceled()", true);
        }
      }
    } catch (error) {
      console.error(error)
      stockMoveError = new StockMoveError(error);
      console.warn(`O estoque da peça de id "${partId}" não foi alterado`)
    } finally {
      await this._movementService.createMovementFromInvoice({
        caller: caller + "; StockController.onOutPutInvoiceCreated()",
        invoice,
        item,
        moveType,
        operation,
        part,
        senderID,
        amount,
        operationFuntion: "Cancelamento",
        OSId,
        orderId,
        isSupplier,
        availableStockBeforeUpdate,
        balanceBeforeUpdate,
        orderStockBeforeUpdate,
        osStockBeforeUpdate,
        error: stockMoveError,
        fromXmlImport,
        amountFromImportedInvoice,
        measurementUnitFromImportedInvoice,
      })
    }
  }

  public async onInvoiceOutCanceled(partId: string, amount: number, movementData: {
    item: InvoiceItem;
    operation: Operation;
    invoice: NF_response;
    senderID: string;
    moveType: MoveTypes;
    caller: string;
    isSupplier: boolean;
    OSId?: string;
    orderId?: string;
  }) {
    const { caller, invoice, item, moveType, operation, senderID, OSId, orderId, isSupplier } = movementData;

    let stockMoveError: StockMoveError,
        part: Part,
        availableStockBeforeUpdate: number,
        balanceBeforeUpdate: number,
        osStockBeforeUpdate: number,
        orderStockBeforeUpdate: number;

    try {
      part = await this.partService.get(partId);
      availableStockBeforeUpdate = part.availableStock;
      balanceBeforeUpdate = part.stock;
      osStockBeforeUpdate = part.osStock;
      orderStockBeforeUpdate = part.orderStock;

      if (operation.changeStock) {
        const isMultiBranch = this.dataService.isMultiBranch();
        
        if (isMultiBranch) {
          // Multi-branch: update BranchPart via API (add because output was canceled)
          await this.partService.adjustBranchPartStock(partId, amount, 'add', 'stock');
        } else {
          // Simple company: update Parts directly
          part.stock = (part.stock || 0) + amount;
          this.calcAvailableStockOf(part, caller);
          await this.partService.addOrUpdate(part, "StockController.onInvoiceOutCanceled()", true);
        }
      }
    } catch (error) {
      console.error(error)
      stockMoveError = new StockMoveError(error);
      console.warn(`O estoque da peça de id "${partId}" não foi alterado`)
    } finally {
      await this._movementService.createMovementFromInvoice({
        caller: caller + "; StockController.onOutPutInvoiceCreated()",
        invoice,
        item,
        moveType,
        operation,
        part,
        senderID,
        amount,
        operationFuntion: "Cancelamento",
        OSId,
        orderId,
        isSupplier,
        availableStockBeforeUpdate,
        balanceBeforeUpdate,
        osStockBeforeUpdate,
        orderStockBeforeUpdate,
        error: stockMoveError,
        fromXmlImport: false
      })
    }
  }

  /** Add requested stock */
  public async onOrderItemAdded(partId: string, amount: number, movementData: {
    order: OrderFull;
    orderItem: OrderItem;
    moveType: MoveTypes;
    operationFunction: OperationFunction;
    caller: string;
    operationId: string;
  }) {
    const { order, orderItem, moveType, operationFunction, caller, operationId } = movementData;

    let stockMoveError: StockMoveError,
        part: Part,
        availableStockBeforeUpdate: number,
        balanceBeforeUpdate: number,
        osStockBeforeUpdate: number,
        orderStockBeforeUpdate: number;

    try {
      part = await this.partService.get(partId);
      availableStockBeforeUpdate = part.availableStock;
      balanceBeforeUpdate = part.stock;
      osStockBeforeUpdate = part.osStock;
      orderStockBeforeUpdate = part.orderStock;

      const isMultiBranch = this.dataService.isMultiBranch();
      
      if (isMultiBranch) {
        // Multi-branch: update BranchPart via API
        await this.partService.adjustBranchPartStock(partId, amount, 'add', 'orderStock');
      } else {
        // Simple company: update Parts directly
        part.orderStock = (part.orderStock || 0) + amount;
        part.availableStock = this.calcAvailableStockOf(part, caller);
        await this.partService.addOrUpdate(part, "StockController.onOrderItemAdded()", true);
      }
    } catch (error) {
      console.error(error);
      stockMoveError = new StockMoveError(error);
    } finally {
      await this._movementService.createMovementFromOrder({
        caller: caller + "; StockController.onOrderItemAdded()",
        moveType,
        operationFunction,
        operationId,
        order,
        orderItem,
        diference: orderItem.amount - orderItem.oldAmount,
        availableStockBeforeUpdate,
        balanceBeforeUpdate,
        osStockBeforeUpdate,
        orderStockBeforeUpdate,
        error: stockMoveError
      });
    }
  }

  /** Remove requested stock */
  public async onOrderItemRemoved(partId: string, amount: number, movementData: {
    order: OrderFull;
    orderItem: OrderItem;
    moveType: MoveTypes;
    operationFunction: OperationFunction;
    caller: string;
    operationId: string;
  }) {
    const { order, orderItem, moveType, operationFunction, caller, operationId } = movementData;

    let stockMoveError: StockMoveError,
        part: Part,
        availableStockBeforeUpdate: number,
        balanceBeforeUpdate: number,
        osStockBeforeUpdate: number,
        orderStockBeforeUpdate: number;

    try {
      part = await this.partService.get(partId);

      availableStockBeforeUpdate = part.availableStock;
      balanceBeforeUpdate = part.stock;
      osStockBeforeUpdate = part.osStock;
      orderStockBeforeUpdate = part.orderStock;

      const isMultiBranch = this.dataService.isMultiBranch();
      
      if (isMultiBranch) {
        // Multi-branch: update BranchPart via API
        await this.partService.adjustBranchPartStock(partId, Math.abs(amount), 'subtract', 'orderStock');
      } else {
        // Simple company: update Parts directly
        part.orderStock = (part.orderStock || 0) - Math.abs(amount);
        part.availableStock = this.calcAvailableStockOf(part, caller);
        await this.partService.addOrUpdate(part, "StockController.onOrderItemRemoved()", true);
      }
    } catch (error) {
      console.error(error);
      stockMoveError = new StockMoveError(error);
    } finally {
      await this._movementService.createMovementFromOrder({
        caller: caller + "; StockController.onOrderItemRemoved()",
        moveType,
        operationFunction,
        operationId,
        order,
        orderItem,
        diference: orderItem.amount - orderItem.oldAmount,
        availableStockBeforeUpdate,
        balanceBeforeUpdate,
        osStockBeforeUpdate,
        orderStockBeforeUpdate,
        error: stockMoveError
      });
    }
  }

  public async onOrderFinished(invoiceItem: InvoiceItem, amount: number, movementData: {
    operation: Operation;
    invoice: NF_response | IssueOrderInvoiceResponse["partInvoice"];
    moveType: MoveTypes;
    orderFull: OrderFull;
    caller: string;
  }) {
    const { caller, invoice, moveType, operation, orderFull } = movementData

    let stockMoveError: StockMoveError,
        part: Part,
        availableStockBeforeUpdate: number,
        balanceBeforeUpdate: number,
        osStockBeforeUpdate: number,
        orderStockBeforeUpdate: number;

    try {
      part = await this.partService.get(invoiceItem.part.id);
      availableStockBeforeUpdate = part.availableStock;
      balanceBeforeUpdate = part.stock;
      osStockBeforeUpdate = part.osStock;
      orderStockBeforeUpdate = part.orderStock;

      if (operation.changeStock) {
        const isMultiBranch = this.dataService.isMultiBranch();
        
        if (isMultiBranch) {
          // Multi-branch: update BranchPart via API
          // Subtract from both stock (balance) and orderStock
          await this.partService.adjustBranchPartStock(invoiceItem.part.id, amount, 'subtract', 'stock');
          await this.partService.adjustBranchPartStock(invoiceItem.part.id, amount, 'subtract', 'orderStock');
        } else {
          // Simple company: update Parts directly
          part.stock = (part.stock || 0) - amount;
          part.orderStock = (part.orderStock || 0) - amount;
          part.availableStock = this.calcAvailableStockOf(part, caller);
          await this.partService.addOrUpdate(part, "StockController.onOrderFinished()", true);
        }
      }
    } catch (error) {
      console.error(error);
      stockMoveError = new StockMoveError(error);
    } finally {
      await this._movementService.createMovementFromInvoice({
        caller: caller + "; StockController.onOrderFinished()",
        invoice,
        operationFuntion: operation.operationFunction,
        item: invoiceItem,
        moveType,
        operation,
        part,
        senderID: orderFull.client,
        orderId: orderFull._id,
        isSupplier: false,
        availableStockBeforeUpdate,
        balanceBeforeUpdate,
        osStockBeforeUpdate,
        orderStockBeforeUpdate,
        error: stockMoveError,
        fromXmlImport: false
      })
    }
  }

  public async onOrderCanceled(partId: string, amount: number, movementData: {
    order: OrderFull | IOrder;
    orderItem: OrderItem | IOrderItem;
    moveType: MoveTypes;
    operationFunction: OperationFunction;
    caller: string;
    operationId: string;
  }) {
    const { caller, moveType, operationFunction, operationId, order, orderItem } = movementData;

    let stockMoveError: StockMoveError,
        part: Part,
        availableStockBeforeUpdate: number,
        balanceBeforeUpdate: number,
        osStockBeforeUpdate: number,
        orderStockBeforeUpdate: number;

    try {
      part = await this.partService.get(partId);
      availableStockBeforeUpdate = part.availableStock;
      balanceBeforeUpdate = part.stock;
      osStockBeforeUpdate = part.osStock;
      orderStockBeforeUpdate = part.orderStock;

      const isMultiBranch = this.dataService.isMultiBranch();
      
      if (isMultiBranch) {
        // Multi-branch: update BranchPart via API
        await this.partService.adjustBranchPartStock(partId, amount, 'subtract', 'orderStock');
      } else {
        // Simple company: update Parts directly
        part.orderStock = (part.orderStock || 0) - amount;
        part.availableStock = this.calcAvailableStockOf(part, caller);
        await this.partService.addOrUpdate(part, "StockController.onOrderCanceled()", true);
      }
    } catch (error) {
      console.error(error);
      stockMoveError = new StockMoveError(error);
    } finally {
      await this._movementService.createMovementFromOrder({
        caller: caller + "; StockController.onOrderCanceled()",
        moveType,
        operationFunction,
        operationId,
        order,
        orderItem,
        diference: orderItem.amount,
        availableStockBeforeUpdate,
        balanceBeforeUpdate,
        osStockBeforeUpdate,
        orderStockBeforeUpdate,
        error: stockMoveError
      });
    }
  }

  public async onPartUpdatedIntoOS(roPart: MkgOSPart, diference: number, movementData: {
    OS: MkgOS;
    caller: string;
    moveType: MoveTypes;
    operationFunction: OperationFunction;
    fieldChanged: FieldChanged;
  }) {
    const { OS, caller, moveType, operationFunction, fieldChanged } = movementData;

    let stockMoveError: StockMoveError,
        part: Part,
        availableStockBeforeUpdate: number,
        balanceBeforeUpdate: number,
        osStockBeforeUpdate: number,
        orderStockBeforeUpdate: number;

    try {
      if (fieldChanged === "amount" && diference !== 0) {
        part = await this.partService.get(roPart.part.id)

        availableStockBeforeUpdate = part.availableStock;
        balanceBeforeUpdate = part.stock;
        osStockBeforeUpdate = part.osStock;
        orderStockBeforeUpdate = part.orderStock;

        const isMultiBranch = this.dataService.isMultiBranch();
        
        if (isMultiBranch) {
          // Multi-branch: update BranchPart via API
          // Difference can be positive (add) or negative (subtract)
          if (diference > 0) {
            await this.partService.adjustBranchPartStock(roPart.part.id, diference, 'add', 'osStock');
          } else {
            await this.partService.adjustBranchPartStock(roPart.part.id, Math.abs(diference), 'subtract', 'osStock');
          }
        } else {
          // Simple company: update Parts directly
          part.osStock = (part.osStock ?? 0) + diference;
          part.availableStock = this.calcAvailableStockOf(part, caller);
          await this.partService.addOrUpdate(part, "StockController.onPartUpdatedIntoOS()", true);
        }
      }
    } catch (error) {
      console.error(error);
      stockMoveError = new StockMoveError(error);
    } finally {
      if (fieldChanged === "amount" && diference !== 0) {
        await this._movementService.createMovementFromOS({
          roPart,
          moveType,
          OS,
          operationFunction,
          part,
          diference,
          availableStockBeforeUpdate,
          balanceBeforeUpdate,
          osStockBeforeUpdate,
          orderStockBeforeUpdate,
          error: stockMoveError
        }, `${caller}; StockController.onPartUpdatedIntoOS(); fieldChanged: ${fieldChanged}`);
      }
    }
  }

  public calcAvailableStockOf(part: Part, caller: string): number {
    part.availableStock = (part.stock || 0) - (part.osStock || 0) - (part.orderStock || 0);
    return part.availableStock;
  }

  private verifyStockMinOf(part: Part) {
    if (part.stock <= (part.stockMin || 0)) {
      const message = this.translateService.instant(`MESSAGE.PART.MIN_STOCK.CONTENT`, { description: part.description });
      const action = this.translateService.instant('ACTION.SHARED.SEE');
      const config: MatSnackBarConfig = {
        duration: 8000,
        horizontalPosition: 'right',
        panelClass: 'snackbar-info',
        verticalPosition: 'top'
      };

      this.snackbar.open(message, action, config).onAction().subscribe(() => {
        this.router.navigate(['/company', this.dataService.company.id, 'parts', part.id])
      })
    }
  }
}